/**
 * CR80 — standard PVC ID/credit-card size used for print and crop (matches common card printers).
 * @see ISO ID-1 is ~85.6×54 mm; CR80 is exactly 3.375″×2.125″.
 */
export const NID_CARD_INCH = { w: 3.375, h: 2.125 };

/** Width ÷ height (exact CR80). */
export const NID_CARD_ASPECT_RATIO = NID_CARD_INCH.w / NID_CARD_INCH.h;

/** Physical size in mm (25.4 mm/in) — derived from CR80 for A4 preview pixel math. */
export const NID_CARD_MM = {
    w: NID_CARD_INCH.w * 25.4,
    h: NID_CARD_INCH.h * 25.4,
};

/** A4 short edge (mm). */
const A4_SHORT_MM = 210;
/** A4 long edge (mm). */
const A4_LONG_MM = 297;

/**
 * Live preview + PDF capture (nid-to-pdf): map mm → px using A4 short edge = 794px.
 * Portrait (794×1123) and landscape (1123×794) share this scale so NID cards render at the same
 * physical mm size in both orientations — landscape does not use a different px/mm basis.
 */
export const NID_A4_PREVIEW_PX_PER_MM = 794 / A4_SHORT_MM;

/** Fixed pixel size for one NID face on the A4 preview canvas (matches print). */
export const NID_CARD_PRINT_PX = {
    w: Math.round(NID_CARD_MM.w * NID_A4_PREVIEW_PX_PER_MM),
    h: Math.round(NID_CARD_MM.h * NID_A4_PREVIEW_PX_PER_MM),
};

/**
 * JPEG size after quad-crop: fixed pixels, exact CR80 ratio (`NID_CARD_ASPECT_RATIO`),
 * independent of quad size. Aligned with pdf `NID_PDF_EXPORT_SCALE` (2) for sharp capture.
 */
export const NID_CARD_CROP_OUTPUT_SCALE = 2;

/** @type {{ w: number, h: number }} */
export const NID_CARD_CROP_OUTPUT_PX = (() => {
    const w = NID_CARD_PRINT_PX.w * NID_CARD_CROP_OUTPUT_SCALE;
    const h = Math.round(w / NID_CARD_ASPECT_RATIO);
    return { w, h };
})();

/**
 * How many copies fit one page. Rotated cards (90/270) stand portrait, and a 4th copy
 * shrinks the grid to where the print reads badly — so a turned card tops out at 3.
 */
export function nidMaxCopies(rotation) {
    const rot = ((Number(rotation) || 0) % 360 + 360) % 360;
    return rot === 90 || rot === 270 ? 3 : 4;
}

/** A4 preview canvas sizes (px); landscape is swap(w,h) of portrait. */
export const NID_PREVIEW_PORTRAIT = {
    w: Math.round(A4_SHORT_MM * NID_A4_PREVIEW_PX_PER_MM),
    h: Math.round(A4_LONG_MM * NID_A4_PREVIEW_PX_PER_MM),
};

export const NID_PREVIEW_LANDSCAPE = {
    w: NID_PREVIEW_PORTRAIT.h,
    h: NID_PREVIEW_PORTRAIT.w,
};
